import {
  DxButton,
  DxCard,
  DxCardContent,
  DxCardHeader,
  DxContainer,
  DxMediaQueryValue,
  DxSpacer,
  DxTag,
} from '@dvag/design-system-react';
import { useAutosave } from '@dvag/dfs-ui-blocks/hooks/useAutosave';
import { UpdateManyResources } from '@dvag/dfs-ui-blocks/hooks/useAutosaveHelper';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { array as yupArray, object as yupObject, string as yupString } from 'yup';

import { SelectedOption, SettingDto } from 'graphql/advisor/generates';
import { useCreateOrUpdateSettings } from 'hooks/useCreateOrUpdateSettings';
import { useGetSettingList } from 'hooks/useGetSettingList';
import { checkIcon } from 'utils/util';
import { AssetsSection } from '../AssetsSection/AssetsSection';
import { FundsSection } from '../FundsSection/FundsSection';
import { QuickAccessSection } from '../QuickAccessSection/QuickAccessSection';
import { StrategySection } from '../StrategySection/StrategySection';

import { UnsavedOrPendingModal } from 'component/UnsavedOrPendingModal';
import i18next from 'i18next';
import { useContinuation } from 'utils/useContinuation';
import '../../style.css';
import { useReadOnlyMode } from '../../../../hooks/useReadOnlyMode';

export const VPDigitalSettings = () => {
  const [webVpSlideView, setWebVpSlideView] = useState('CONTRACT_VIEW');
  const [customerViewType, setCustomerViewType] = useState('EXPANDED_VIEW');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isReadMode = useReadOnlyMode();

  const settingList = useGetSettingList();
  const createOrUpdateUserSettings = useCreateOrUpdateSettings();

  const { continuation, getCanContinueHandler, onContinuationRequest } = useContinuation();
  const onCanContinueChange = useMemo(() => getCanContinueHandler(), [getCanContinueHandler]);

  const resourceSchema = useMemo(
    () =>
      yupObject().shape({
        id: yupString().nullable(),
        selectedOptions: yupArray(),
        code: yupString(),
      }),
    [],
  );

  const { setValue, getValues, resources, flushChanges } = useAutosave<SettingDto>({
    data: settingList.data,
    debounceTime: 0,
    onIsDirtyChange: (isDirtyParam: boolean) => {
      if (!isDirtyParam) {
        getCanContinueHandler();
      }
      onCanContinueChange(!isDirtyParam);
    },
    onIsSubmittingChange: setIsSubmitting,
    updateManyResources: createOrUpdateUserSettings.mutateAsync as UpdateManyResources<SettingDto>,
    resourceSchema,
  });

  useEffect(() => {
    onContinuationRequest(flushChanges);
  }, [flushChanges, onContinuationRequest]);

  const findSettingIndex = useCallback(
    (code: string) => {
      return resources?.findIndex((setting) => setting.code === code) ?? -1;
    },
    [resources],
  );

  const getSettingOption = useCallback(
    (code: string) => {
      const index = findSettingIndex(code);
      return index !== -1 ? getValues('selectedOptions', index)?.[0] : null;
    },
    [findSettingIndex, getValues],
  );

  const updateSetting = useCallback(
    (code: string, value: SelectedOption | string) => {
      const index = findSettingIndex(code);
      if (index !== -1) {
        setValue('selectedOptions', [value], index);
      }
    },
    [findSettingIndex, setValue],
  );

  useEffect(() => {
    if (resources?.length) {
      const assetsOption = getSettingOption('WEB_VP_SLIDE');

      if (assetsOption === SelectedOption.ContractView) {
        setWebVpSlideView('CONTRACT_VIEW');
      } else if (
        assetsOption === SelectedOption.CustomerView ||
        assetsOption === 'CUSTOMER_VIEW_MINIMAL'
      ) {
        setWebVpSlideView('CUSTOMER_VIEW');
        setCustomerViewType(
          assetsOption === 'CUSTOMER_VIEW_MINIMAL' ? 'REDUCED_VIEW' : 'EXPANDED_VIEW',
        );
      }
    }
  }, [resources, getSettingOption]);

  const strategyEnabled = getSettingOption('CONCEPT_WITH_STRATEGY') === SelectedOption.Pyramid;
  const hideQuickAccessEnabled = getSettingOption('QUICK_ACCESS_TOGGLE') === SelectedOption.On;
  const fundsEnabled = getSettingOption('FONDS_TOOL') === SelectedOption.On;
  const assetsEnabled = getSettingOption('WEB_VP_SLIDE') !== SelectedOption.Off;
  const isStrategyPersonalized =
    getSettingOption('CONCEPT_WITH_STRATEGY') !== SelectedOption.Pyramid;
  const isPersonalized =
    isStrategyPersonalized || hideQuickAccessEnabled || assetsEnabled || fundsEnabled;

  const handleStrategyToggle = (checked: boolean) => {
    updateSetting(
      'CONCEPT_WITH_STRATEGY',
      checked ? SelectedOption.Pyramid : SelectedOption.AdvisoryModules,
    );
  };

  const handleQuickAccessToggle = (checked: boolean) => {
    updateSetting('QUICK_ACCESS_TOGGLE', checked ? SelectedOption.On : SelectedOption.Off);
  };

  const handleFundsToolToggle = (checked: boolean) => {
    updateSetting('FONDS_TOOL', checked ? SelectedOption.On : SelectedOption.Off);
  };

  const handleAssetsOff = () => {
    updateSetting('WEB_VP_SLIDE', SelectedOption.Off);
  };

  const handleAssetsContractView = () => {
    setWebVpSlideView('CONTRACT_VIEW');
    updateSetting('WEB_VP_SLIDE', SelectedOption.ContractView);
  };

  const handleViewChange = (value: string) => {
    setWebVpSlideView(value);

    let selectedOption: string;
    if (value === 'CONTRACT_VIEW') {
      selectedOption = SelectedOption.ContractView;
    } else {
      selectedOption =
        customerViewType === 'REDUCED_VIEW' ? 'CUSTOMER_VIEW_MINIMAL' : SelectedOption.CustomerView;
    }

    updateSetting('WEB_VP_SLIDE', selectedOption);
  };

  const handleViewTypeChange = (value: string) => {
    setCustomerViewType(value);

    if (webVpSlideView === 'CUSTOMER_VIEW') {
      const selectedOption =
        value === 'REDUCED_VIEW' ? 'CUSTOMER_VIEW_MINIMAL' : SelectedOption.CustomerView;

      updateSetting('WEB_VP_SLIDE', selectedOption);
    }
  };

  const handleResetAll = () => {
    if (isReadMode) {
      return;
    }

    setWebVpSlideView('CONTRACT_VIEW');
    setCustomerViewType('EXPANDED_VIEW');
    const defaults = {
      CONCEPT_WITH_STRATEGY: SelectedOption.Pyramid,
      QUICK_ACCESS_TOGGLE: SelectedOption.Off,
      WEB_VP_SLIDE: SelectedOption.Off,
      FONDS_TOOL: SelectedOption.Off,
    };

    resources?.forEach((setting, index) => {
      const defaultValue = defaults[setting.code as keyof typeof defaults];
      if (defaultValue) {
        setValue('selectedOptions', [defaultValue], index);
      }
    });
  };

  return (
    <>
      <DxCard data-testid="vpdigital-settings-card" className="vpdigital-settings-card">
        <DxCardHeader
          slot="header"
          label={i18next.t('vpDigitalSettings.page.title')}
          sublabel={i18next.t('vpDigitalSettings.page.subtitle')}
        >
          <DxMediaQueryValue
            mq1="true"
            mq2="true"
            mq3="false"
            mq4="false"
            mq5="false"
            property="contentbelow"
          />
          <DxContainer
            color="transparent"
            style={{
              display: 'flex',
              gap: '16px',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
            }}
          >
            <DxButton
              data-testid="settings-reset-button"
              onClick={handleResetAll}
              icon={checkIcon('wiedergabe')}
              iconposition="right"
              type="text"
              label={i18next.t('vpDigitalSettings.page.resetToDefault')}
              disabled={isReadMode}
            />
            {isPersonalized && (
              <DxTag theme="orange" label={i18next.t('vpDigitalSettings.page.personalized')} />
            )}
          </DxContainer>
        </DxCardHeader>

        <DxCardContent>
          <DxContainer>
            <StrategySection
              enabled={strategyEnabled}
              onToggle={handleStrategyToggle}
              readMode={isReadMode}
            />
            <DxContainer className="settings-section-separator" />
            <QuickAccessSection
              enabled={hideQuickAccessEnabled}
              onToggle={handleQuickAccessToggle}
              readMode={isReadMode}
            />
            <DxContainer className="settings-section-separator" />
            <AssetsSection
              enabled={assetsEnabled}
              onToggle={(checked) => (checked ? handleAssetsContractView() : handleAssetsOff())}
              viewType={webVpSlideView}
              customerViewType={customerViewType}
              onViewChange={handleViewChange}
              onViewTypeChange={handleViewTypeChange}
              readMode={isReadMode}
            />
            <DxContainer className="settings-section-separator" />
            <FundsSection
              enabled={fundsEnabled}
              onToggle={handleFundsToolToggle}
              readMode={isReadMode}
            />
            <DxSpacer size="16" />
          </DxContainer>
        </DxCardContent>
      </DxCard>
      <UnsavedOrPendingModal
        showPending={continuation !== undefined && isSubmitting}
        showUnsaved={continuation !== undefined && !isSubmitting}
        onCancel={continuation?.onCancel}
        onConfirm={continuation?.onConfirm}
      />
    </>
  );
};
